/**
 * A value on one line. When it doesn't fit, it ends in "…": hovering shows the
 * whole value, and clicking "…" opens it in place ("less" closes it again).
 *
 * The fit is measured, not guessed — the value is cut only when its line is
 * actually wider than its box, re-checked whenever the box resizes.
 */
import React, { useLayoutEffect, useRef, useState } from "react";
import { Tooltip } from "antd";

export default function ClampedText({ text, className }) {
  const value = text == null ? "" : String(text);
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || open) return undefined;
    let live = true;
    const measure = () => { if (live) setOverflows(el.scrollWidth > el.clientWidth + 1); };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    if (observer) observer.observe(el);
    return () => {
      live = false;
      if (observer) observer.disconnect();
    };
  }, [value, open]);

  if (open) {
    return (
      <span className={["bs-clamp", "open", className].filter(Boolean).join(" ")}>
        {value}
        <button type="button" className="bs-more" aria-expanded="true" onClick={() => setOpen(false)}>less</button>
      </span>
    );
  }
  const line = (
    <span className={["bs-clamp", overflows ? "cut" : "", className].filter(Boolean).join(" ")}>
      <span ref={ref} className="bs-clamp-text">{value}</span>
      {overflows ? (
        <button type="button" className="bs-more" aria-expanded="false" aria-label={`Show all: ${value}`}
          onClick={() => setOpen(true)}>…</button>
      ) : null}
    </span>
  );
  // Always inside the Tooltip (titled only when cut): wrapping it on overflow
  // remounted the measured span, the observer kept watching the detached one,
  // and its zero width switched "cut" straight back off.
  return (
    <Tooltip title={overflows ? value : ""} placement="topLeft" overlayStyle={{ maxWidth: 420 }}>{line}</Tooltip>
  );
}
